/**
 * Configuration for negative pathway content
 * Each key represents a unique negative pathway scenario
 */
export const negativePathwayContent = {
  q1_neither: {
    questionNumber: 1,
    questionText: "To start with, please tell us what is your complaint about?",
    selectedOption: "Neither of these",
    introText: "Unfortunately we can only help with complaints about the NHS in England or a UK government department or public organisation.",
    helpText: "Here is some information about who can help if you want to complain about:",
    links: [
      {
        label: "A charity",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/charities",
        description: null
      },
      {
        label: "A consumer problem",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/consumer-problems",
        description: "about goods or services you have bought from a private company or individual."
      },
      {
        label: "Financial services",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/financial-services",
        description: null
      },
      {
        label: "Gas, electricity and water",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/gas-electricity-and-water",
        description: null
      },
      {
        label: "Housing",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/housing",
        description: null
      },
      {
        label: "Lawyers and solicitors",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/lawyers-and-solicitors",
        description: null
      },
      {
        label: "A local council",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/local-councils",
        description: "including council tax, council housing, planning and social care."
      },
      {
        label: "Members of Parliament",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/members-parliament",
        description: null
      },
      {
        label: "Postal services",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/postal-services",
        description: null
      },
      {
        label: "Private health",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/private-healthcare",
        description: null
      },
      {
        label: "Social care",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/social-care",
        description: null
      },
      {
        label: "Telecommunications",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/telecommunications",
        description: "for example phone and internet companies"
      },
      {
        label: "Television, radio, newspapers and advertising",
        href: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/television-radio-newspapers-and-advertising",
        description: "including ombudsmen in Scotland, Wales and Northern Ireland."
      }
    ],
    additionalContent: [
      {
        type: "paragraph",
        content: "Here is a list of other",
        linkText: "ombudsmen.",
        linkHref: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/other-ombudsmen"
      },
      {
        type: "paragraph",
        content: "If you can't find an organisation to help you, please contact your local",
        linkText: "Citizens Advice for help.",
        linkHref: "https://www.citizensadvice.org.uk/"
      }
    ]
  },

  q2_employment: {
    questionNumber: 2,
    questionText: "Do you work for the organisation and want to complain about an employment matter?",
    selectedOption: "Yes",
    introText: "Unfortunately we can't help with personal employment matters.",
    helpText: "Please talk to your organisation's HR department or your union or professional organisation.",
    links: [],
    additionalContent: [
      {
        type: "paragraph",
        content: "Alternatively, the",
        linkText: "Advisory, Conciliation and Arbitration Service (ACAS)",
        linkHref: "https://www.acas.org.uk/index.aspx?articleid=1461",
        afterLinkText: "or",
        secondLinkText: "Citizens Advice",
        secondLinkHref: "https://www.citizensadvice.org.uk/",
        endText: "may be able to help you."
      }
    ]
  },

  q4_not_nhs_care: {
    questionNumber: 4,
    questionText: "Is your complaint about care received from the NHS?",
    selectedOption: "No",
    introText: "Unfortunately we can't look into complaints about private healthcare, unless the NHS paid for it.",
    helpText: "We can't arrange treatment. You should contact your local GP or 111 service.",
    links: [],
    additionalContent: [
      {
        type: "section",
        title: "Mental Health Act",
        content: "We can't usually look at complaints about care and treatment under the Mental Health Act. More information on",
        linkText: "our website.",
        linkHref: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/mental-health-act"
      },
      {
        type: "paragraph",
        content: "Find out who can help with",
        linkText: "private healthcare complaints.",
        linkHref: "https://www.ombudsman.org.uk/making-complaint/if-we-cant-help/private-healthcare"
      },
      {
        type: "paragraph",
        content: "If you are unsure or have questions please ring us on 0345 015 4033"
      }
    ]
  },

  q5_not_complained_nhs: {
    questionNumber: 5,
    questionText: "Have you complained directly to the organisation?",
    selectedOption: "No",
    introText: "You should complain to the organisation first and give them a chance to put things right.",
    helpText: null,
    links: [],
    additionalContent: [
      {
        type: "paragraph",
        linkText: "Find out more about how to do this.",
        linkHref: "https://www.ombudsman.org.uk/making-complaint/what-do-you-come-us"
      }
    ]
  },

  q5_not_complained_parliamentary: {
    questionNumber: 5,
    questionText: "Have you complained directly to the organisation?",
    selectedOption: "No",
    introText: "You should complain to the organisation first and give them a chance to put things right.",
    helpText: null,
    links: [],
    additionalContent: [
      {
        type: "paragraph",
        linkText: "Find out more about how to do this.",
        linkHref: "https://www.ombudsman.org.uk/making-complaint/what-do-you-come-us"
      },
      {
        type: "section",
        title: "Second tier:",
        content: "Organisations like the Child Support Agency, Jobcentre Plus, DVLA and HMRC offer a second review of your complaint. You should take up this offer before we will consider your complaint."
      },
      {
        type: "paragraph",
        content: "For help with making a complaint to the organisation, visit your",
        linkText: "local Citizens Advice.",
        linkHref: "https://www.citizensadvice.org.uk/"
      }
    ]
  },

  q6_incomplete_nhs: {
    questionNumber: 6,
    questionText: "Have you completed their complaints process?",
    selectedOption: "No",
    introText: "We are the last stage in the complaints process. You should complete the NHS complaints process first and get a final decision from them.",
    helpText: "This will usually be in a letter which says that, if you are not happy with their decision, you can complain to us. If you are not sure if you have had a final decision yet, please check with the organisation. If you're still not sure, please contact us on 0345 015 4033.",
    links: [],
    additionalContent: [
      {
        type: "section",
        title: "Need help with your complaint?",
        linkText: "You can contact an advocacy group",
        linkHref: "https://www.ombudsman.org.uk/making-complaint/getting-support-and-help",
        afterLinkText: "for free advice and support."
      },
      {
        type: "section",
        title: "Organisation not replied in the time they said they would?",
        content: "If you have not had a decision for more than six months, the organisation should tell you why. Please contact them if there is a delay. If you haven't had a reply after six months, you can also ring us on 0345 015 4033 and we will see how we can help."
      }
    ]
  },

  q6_incomplete_parliamentary: {
    questionNumber: 6,
    questionText: "Have you completed their complaints process?",
    selectedOption: "No",
    introText: "We are the last stage in the complaints process. You should complete the organisation's complaint process first and get a final response from them.",
    helpText: "The organisation should give you a decision. Usually this will be in a letter which tells you that, if you are not happy with their decision, you can complain to us.",
    links: [],
    additionalContent: [
      {
        type: "section",
        title: "Not sure if you have completed the complaints process?",
        content: "Please check with the organisation. If you're still not sure, please contact us on 0345 015 4033."
      },
      {
        type: "paragraph",
        content: "In some cases, you may have to go through a second review, often called a 'second tier'."
      },
      {
        type: "section",
        title: "Need help with your complaint?",
        content: "Contact your local",
        linkText: "Citizens Advice",
        linkHref: "https://www.citizensadvice.org.uk/",
        afterLinkText: "or MP. MPs will consider all complaints, no matter how big or small. You can",
        secondLinkText: "find out who your MP is here.",
        secondLinkHref: "https://www.parliament.uk/mps-lords-and-offices/mps/"
      },
      {
        type: "section",
        title: "Organisation not replied in the time they said they would?",
        linkText: "Please contact your MP.",
        linkHref: "https://www.parliament.uk/mps-lords-and-offices/mps/"
      }
    ]
  }
};

/**
 * Helper function to get the correct content key based on question and jurisdiction
 */
export const getContentKey = (questionNumber, selection, jurisdiction = null) => {
  const keyMap = {
    1: { neither: 'q1_neither' },
    2: { yes: 'q2_employment' },
    4: { no: 'q4_not_nhs_care' },
    5: { 
      no: jurisdiction === 'parliamentary' ? 'q5_not_complained_parliamentary' : 'q5_not_complained_nhs'
    },
    6: {
      no: jurisdiction === 'parliamentary' ? 'q6_incomplete_parliamentary' : 'q6_incomplete_nhs'
    }
  };

  return keyMap[questionNumber]?.[selection] || null;
};
